/* selv-atelier.js (v18) : barre de menu et panneau « L'Atelier » au bureau (≥ 990 px), écrans du tiroir au téléphone.
   - Les panneaux sont rendus par sections/selv-atelier.liquid dans des <template> ; ils sont placés dans leur entrée de
     menu à la première intention (pointeur sur la barre, focus) ou quand le navigateur est libre après le chargement.
     Seules les images de la 1re catégorie se chargent alors ; celles des autres catégories et des cartes du panneau
     compact attendent l'ouverture du panneau ou le choix de leur catégorie.
   - Souris : ouverture après un court délai d'intention (immédiate si un panneau est déjà ouvert), fermeture différée ;
     le pointeur peut traverser une entrée voisine, ou couper en diagonale vers les sous-liens, sans tout fermer ni
     changer de catégorie en chemin.
   - Toucher (tablette ≥ 990 px) : le 1er toucher ouvre, le 2e suit le lien ; même chose pour chaque catégorie.
   - Clavier (modèle « disclosure ») : le focus seul n'ouvre rien ; Flèche bas sur l'entrée, ou Entrée sur son bouton,
     ouvre ; flèches dans les catégories, les sous-liens, la colonne vedette et la bande ; Échap ferme et rend le focus.
   - Goutte olive sous l'entrée survolée ; langue et devise réunies en une pastille (soumet les formulaires du thème),
     refermée dès qu'un panneau s'ouvre.
   - Barre collante : l'îlot se prolonge dans le panneau (classe atl-full-open sur <html>). */
(() => {
  if (window.__selvAtelier) return;
  window.__selvAtelier = 1;

  const doc = document;
  const root = doc.documentElement;
  const desk = () => matchMedia('(min-width: 990px)').matches;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const txt = (el) => (el ? el.textContent : '').replace(/\s+/g, ' ').trim();
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const path = (u) => { try { return new URL(u, location.href).pathname.replace(/\/$/, ''); } catch (e) { return u; } };
  const isMouse = (e) => e.pointerType === 'mouse' || e.pointerType === 'pen';
  const ric = 'requestIdleCallback' in window;

  let teardowns = [];
  const on = (el, ev, fn, opt) => { el.addEventListener(ev, fn, opt); teardowns.push(() => el.removeEventListener(ev, fn, opt)); };
  let closeLoc = null;

  // Images en attente : sans src tant que leur catégorie n'est pas montrée (le visibility:hidden du panneau ne suffit
  // pas à retarder le chargement paresseux du navigateur)
  const park = (scope) => scope.querySelectorAll('img[src]').forEach((im) => {
    im.dataset.src = im.getAttribute('src');
    im.removeAttribute('src');
    if (im.hasAttribute('srcset')) { im.dataset.srcset = im.getAttribute('srcset'); im.removeAttribute('srcset'); }
  });
  const wake = (scope) => {
    if (!scope) return;
    scope.querySelectorAll('img[data-src]').forEach((im) => {
      if (im.dataset.srcset) im.setAttribute('srcset', im.dataset.srcset);
      im.setAttribute('src', im.dataset.src);
      delete im.dataset.src;
      delete im.dataset.srcset;
    });
  };

  const init = () => {
    const header = doc.querySelector('header.site-header');
    const nav = doc.getElementById('AccessibleNav');
    const wrapNav = doc.getElementById('top_links_wrapper') || (nav && nav.parentElement);
    if (!header || !nav) return;
    const tpls = [doc.getElementById('SelvAtelierTpl'), doc.getElementById('SelvAtelierMiniTpl')].filter(Boolean);
    if (!tpls.length) return;
    root.classList.add('atl-ready');

    const items = [...nav.children].filter((li) => li.matches('li'));
    const hostFor = (tpl) => {
      const want = path(tpl.dataset.atlParent || '');
      return items.find((li) => {
        const a = li.querySelector(':scope > a');
        if (!a) return false;
        const p = path(a.getAttribute('href') || '');
        return p === want || p.endsWith(want);
      });
    };
    const panels = [];

    /* ---------- voile (reste posé un instant entre deux panneaux : pas de clignement) ---------- */
    const veil = doc.createElement('div');
    veil.className = 'atl-veil';
    veil.setAttribute('aria-hidden', 'true');
    header.parentElement.insertBefore(veil, header.parentElement.firstChild);
    let tVeil = 0;
    const syncVeil = () => {
      clearTimeout(tVeil);
      if (panels.some((o) => o.open)) root.classList.add('atl-veil-on');
      else tVeil = setTimeout(() => root.classList.remove('atl-veil-on'), 160);
    };
    teardowns.push(() => { clearTimeout(tVeil); veil.remove(); });

    /* ---------- goutte olive sous l'entrée survolée ---------- */
    const ind = doc.createElement('span');
    ind.className = 'atl-ind is-snap';
    ind.setAttribute('aria-hidden', 'true');
    wrapNav.appendChild(ind);
    teardowns.push(() => ind.remove());
    let indAt = null;
    const moveInd = (li) => {
      if (!li) { ind.classList.remove('is-on'); indAt = null; return; }
      const t = li.querySelector(':scope > a .site-nav--link-text') || li.querySelector(':scope > a');
      if (!t) return;
      const r = t.getBoundingClientRect();
      const w = wrapNav.getBoundingClientRect();
      ind.style.setProperty('--x', `${Math.round(r.left - w.left + r.width / 2 - 3.5)}px`);
      ind.style.setProperty('--y', `${Math.round(r.bottom - w.top + 7)}px`);
      if (!indAt) { ind.classList.add('is-snap'); void ind.offsetWidth; }
      ind.classList.toggle('is-snap', !indAt);
      ind.classList.add('is-on');
      indAt = li;
      requestAnimationFrame(() => ind.classList.remove('is-snap'));
    };
    let openHost = null;
    items.forEach((li) => {
      on(li, 'pointerenter', (e) => { if (isMouse(e) && desk()) moveInd(li); });
      on(li, 'focusin', () => { if (desk()) moveInd(li); });
    });
    on(nav, 'pointerleave', (e) => { if (isMouse(e)) setTimeout(() => moveInd(openHost), 60); });
    on(nav, 'focusout', (e) => { if (!nav.contains(e.relatedTarget)) moveInd(openHost); });
    on(window, 'resize', () => { if (indAt) { indAt = null; moveInd(openHost); } }, { passive: true });

    /* ---------- géométrie : panneau aligné sur l'îlot de la barre ---------- */
    const geo = (host, panel, mini) => {
      const hr = header.getBoundingClientRect();
      const lr = host.getBoundingClientRect();
      const ps = getComputedStyle(header, '::before');
      const il = parseFloat(ps.left) || 40;
      const ir = parseFloat(ps.right) || il;
      const ib = parseFloat(ps.bottom) || 8;
      const it = parseFloat(ps.top) || 8;
      const pillH = hr.height - it - ib;
      panel.style.setProperty('--atl-dy', `${Math.round(hr.bottom - lr.top)}px`);
      panel.style.setProperty('--atl-dy-s', `${Math.round(hr.bottom - ib - lr.top)}px`);
      panel.style.setProperty('--atl-gap', `${Math.round(hr.bottom - lr.bottom + 16)}px`);
      panel.style.setProperty('--atl-vb', `${Math.round(hr.bottom)}px`);
      // passerelle entre l'entrée et le panneau, portée par l'entrée elle-même (le panneau peut défiler et la couper)
      const top = root.classList.contains('lq-scrolled') && !mini ? hr.bottom - ib : hr.bottom + 14;
      host.style.setProperty('--atl-bridge', `${Math.max(0, Math.round(top - lr.bottom)) + 2}px`);
      header.style.setProperty('--atl-pr', `${Math.round(pillH / 2)}px`);
      if (!mini) {
        panel.style.setProperty('--atl-x', `${Math.round(hr.left + il - lr.left)}px`);
        panel.style.setProperty('--atl-w', `${Math.round(hr.width - il - ir)}px`);
      } else {
        const pw = panel.offsetWidth || 560;
        let x = lr.left + lr.width / 2 - pw / 2;
        x = Math.max(hr.left + il, Math.min(x, hr.right - ir - pw));
        panel.style.setProperty('--atl-mx', `${Math.round(x - lr.left)}px`);
      }
    };

    /* ---------- pointeur dans la barre : vise-t-il un panneau ouvert en descendant ? ---------- */
    const navPts = [];
    on(header, 'pointermove', (e) => { navPts.push([e.clientX, e.clientY]); if (navPts.length > 4) navPts.shift(); }, { passive: true });
    const headingInto = (o) => {
      const a = navPts[0];
      const b = navPts[navPts.length - 1];
      if (!a || !b || a === b || !o.panel) return false;
      const dy = b[1] - a[1];
      if (dy <= 0) return false;
      const r = o.panel.getBoundingClientRect();
      const k = Math.max(0, (r.top - b[1]) / dy);
      const x = b[0] + (b[0] - a[0]) * k;
      return x >= r.left && x <= r.right;
    };

    /* ---------- un panneau par modèle ---------- */
    const ARROW = '<svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true" focusable="false"><path d="M5.5 7.5 10 12l4.5-4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const vis = (a) => a.getClientRects().length > 0 && getComputedStyle(a).visibility !== 'hidden';
    const cyc = (list, i, d) => list[(i + d + list.length) % list.length];
    tpls.forEach((tpl) => {
      const host = hostFor(tpl);
      if (!host) return;
      const mini = tpl.id === 'SelvAtelierMiniTpl';
      const topLink = host.querySelector(':scope > a');
      if (!topLink) return;
      // Le bouton de sous-menu (lq-dd-btn) est ajouté plus tard par selv-liquid.js : le chercher au moment voulu
      const btnOf = () => host.querySelector(':scope > .lq-dd-btn');
      host.classList.add(mini ? 'atl-host-mini' : 'atl-host');
      const st = { host, mini, panel: null, cats: [], open: false, tClose: 0, tOpen: 0, aim: 0, tWake: 0, hov: null, touchAt: 0 };
      panels.push(st);

      const build = () => {
        if (st.panel) return;
        const frag = tpl.content.cloneNode(true);
        const pnl = frag.querySelector('.atl');
        if (!pnl) return;
        if (mini) park(pnl);
        else [...pnl.querySelectorAll('.atl-cat')].slice(1).forEach(park);
        host.appendChild(frag);
        st.panel = pnl;
        pnl.inert = true;
        const br = doc.createElement('span');
        br.className = 'atl-bridge';
        br.setAttribute('aria-hidden', 'true');
        host.appendChild(br);
        teardowns.push(() => br.remove());
        st.cats = [...pnl.querySelectorAll('.atl-cat')];
        let cur = st.cats[0] || null;
        st.cats.forEach((c, i) => {
          const a = c.querySelector('.atl-cat__a');
          const sub = c.querySelector('.atl-sub');
          if (sub) { sub.id = `${pnl.id}-c${i}`; a.setAttribute('aria-controls', sub.id); }
          a.setAttribute('aria-expanded', String(c === cur));
        });
        const setCat = (el) => {
          if (!el || el === cur) return;
          wake(el);
          const dir = cur && st.cats.indexOf(el) < st.cats.indexOf(cur) ? '-8px' : '8px';
          pnl.style.setProperty('--atl-dir', dir);
          st.cats.forEach((c) => {
            c.classList.toggle('is-on', c === el);
            c.querySelector('.atl-cat__a').setAttribute('aria-expanded', String(c === el));
          });
          cur = el;
        };
        st.setCat = setCat;
        st.cur = () => cur;

        // Visée : en route vers les sous-liens, le pointeur peut frôler une autre catégorie sans la choisir
        const pts = [];
        let lastAim = null;
        on(pnl, 'pointermove', (e) => { pts.push([e.clientX, e.clientY]); if (pts.length > 4) pts.shift(); }, { passive: true });
        const aiming = () => {
          const z = cur && cur.querySelector('.atl-sub');
          const loc = pts[pts.length - 1];
          const prev = pts[0];
          if (!z || !loc || !prev || prev === loc) return false;
          const r = z.getBoundingClientRect();
          if (!r.width || loc[0] >= r.left || prev[0] >= r.left) return false;
          if (lastAim && lastAim[0] === loc[0] && lastAim[1] === loc[1]) return false;
          const slope = (a, b) => (b[1] - a[1]) / (b[0] - a[0]);
          const up = [r.left, r.top - 40];
          const lo = [r.left, r.bottom + 40];
          return slope(loc, up) < slope(prev, up) && slope(loc, lo) > slope(prev, lo);
        };
        const choose = (c) => {
          clearTimeout(st.aim);
          if (st.hov !== c) return;
          if (aiming()) { lastAim = pts[pts.length - 1]; st.aim = setTimeout(() => choose(c), 110); return; }
          lastAim = null;
          setCat(c);
        };
        st.cats.forEach((c) => {
          const a = c.querySelector('.atl-cat__a');
          on(a, 'pointerenter', (e) => { if (!isMouse(e)) return; st.hov = c; clearTimeout(st.aim); st.aim = setTimeout(() => choose(c), 40); });
          on(a, 'pointerleave', () => { if (st.hov === c) st.hov = null; });
          // focus au clavier seulement : un toucher donne aussi le focus au lien, et la catégorie serait déjà choisie au clic
          on(a, 'focus', () => { if (a.matches(':focus-visible')) setCat(c); });
          // Toucher : le 1er toucher montre la catégorie, le 2e suit le lien
          on(a, 'click', (e) => { if (root.classList.contains('atl-touch') && c !== cur) { e.preventDefault(); setCat(c); } });
        });
        geo(host, pnl, mini);
      };
      st.build = build;

      const setOpen = (v) => {
        clearTimeout(st.tClose);
        clearTimeout(st.tOpen);
        if (v === st.open) return;
        if (v) {
          build();
          if (!st.panel) return;
          panels.forEach((o) => { if (o !== st && o.open) o.setOpen(false); });
          if (closeLoc) closeLoc();
          geo(host, st.panel, mini);
          clearTimeout(st.tWake);
          if (mini) wake(st.panel);
          else st.tWake = setTimeout(() => wake(st.panel), 200);
        }
        st.open = v;
        // fermé, le panneau ne prend plus le focus, même pendant son fondu de sortie
        st.panel.inert = !v;
        host.classList.toggle('atl-on', v);
        // Flow marque l'entrée touchée (nav-hover) ; à la fermeture, le cycle « 1er toucher ouvre » repart de zéro
        if (!v) host.classList.remove('nav-hover');
        root.classList.toggle(mini ? 'atl-mini-open' : 'atl-full-open', v);
        const b = btnOf();
        if (b) {
          b.setAttribute('aria-expanded', String(v));
          if (st.panel) b.setAttribute('aria-controls', st.panel.id);
        }
        const other = panels.find((o) => o.open);
        openHost = v ? host : (other ? other.host : null);
        if (!v) {
          clearTimeout(st.aim);
          st.hov = null;
          if (st.setCat && st.cats.length) st.setCat(st.cats[0]);
          if (!nav.matches(':hover')) moveInd(openHost);
        }
        syncVeil();
      };
      st.setOpen = setOpen;
      st.later = () => { clearTimeout(st.tClose); st.tClose = setTimeout(() => setOpen(false), 240); };

      /* Souris : court délai d'intention (un balayage de la barre n'ouvre rien), passage immédiat d'un panneau à l'autre */
      on(host, 'pointerenter', (e) => {
        if (!isMouse(e) || !desk()) return;
        clearTimeout(st.tClose);
        if (mini && st.panel) wake(st.panel);
        if (st.open) return;
        clearTimeout(st.tOpen);
        const other = panels.find((o) => o.open && o !== st);
        if (!other) { st.tOpen = setTimeout(() => setOpen(true), 90); return; }
        // un autre panneau est ouvert : le pointeur qui ne fait que traverser cette entrée en descendant vers lui ne
        // change pas de panneau ; posé ici, il bascule aussitôt
        let last = null;
        const tryOpen = () => {
          const b = navPts[navPts.length - 1];
          if (headingInto(other) && !(last && b && last[0] === b[0] && last[1] === b[1])) { last = b; st.tOpen = setTimeout(tryOpen, 90); return; }
          setOpen(true);
        };
        st.tOpen = setTimeout(tryOpen, 40);
      });
      on(host, 'pointerleave', (e) => { if (isMouse(e)) { clearTimeout(st.tOpen); st.later(); } });
      on(host, 'focusout', (e) => { if (st.open && !host.contains(e.relatedTarget) && !host.matches(':hover')) setOpen(false); });

      /* Toucher : Flow annule le 1er touchstart de l'entrée (aucun clic) ; le panneau s'ouvre donc au touchend */
      let tx = 0;
      let ty = 0;
      on(topLink, 'touchstart', (e) => { const t = e.touches[0]; tx = t.clientX; ty = t.clientY; }, { passive: true });
      on(topLink, 'touchend', (e) => {
        if (!desk() || st.open) return;
        const t = e.changedTouches[0];
        if (Math.abs(t.clientX - tx) > 10 || Math.abs(t.clientY - ty) > 10) return;
        root.classList.add('atl-touch');
        st.touchAt = performance.now();
        setOpen(true);
      }, { passive: true });
      on(topLink, 'click', (e) => {
        if (!desk()) return;
        // le clic qui suit le toucher d'ouverture (quand Flow ne l'a pas annulé) ne doit pas suivre le lien
        if (performance.now() - st.touchAt < 400) { e.preventDefault(); return; }
        if (!st.open && root.classList.contains('atl-touch')) { e.preventDefault(); setOpen(true); }
      });

      /* Clavier */
      on(host, 'click', (e) => {
        const b = e.target.closest && e.target.closest('.lq-dd-btn');
        if (!b || b.parentElement !== host || !desk()) return;
        // phase de capture : le bouton de selv-liquid.js ne reçoit pas ce clic (sinon il inverserait aria-expanded)
        e.preventDefault();
        e.stopPropagation();
        setOpen(!st.open);
      }, true);
      on(host, 'keydown', (e) => {
        if (!desk()) return;
        if (e.key === 'Escape' && st.open) {
          e.stopPropagation();
          setOpen(false);
          topLink.focus();
          return;
        }
        if (!/^Arrow(Down|Up|Left|Right)$/.test(e.key)) return;
        const k = e.key;
        const t = e.target;
        if ((t === topLink || t === btnOf()) && k === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          const first = st.panel && st.panel.querySelector('.atl-cat.is-on .atl-cat__a, .atl-cat__a, .atl-brand');
          if (first) first.focus();
          return;
        }
        if (!st.panel || !st.panel.contains(t)) return;
        // dans le panneau, les flèches ne font jamais défiler la page derrière
        e.preventDefault();
        const links = st.cats.map((c) => c.querySelector('.atl-cat__a'));
        const ci = links.indexOf(t);
        if (ci > -1) {
          if (k === 'ArrowDown') cyc(links, ci, 1).focus();
          else if (k === 'ArrowUp') cyc(links, ci, -1).focus();
          else if (k === 'ArrowRight') { const f = [...st.cats[ci].querySelectorAll('.atl-sub a')].find(vis); if (f) f.focus(); }
          else topLink.focus();
          return;
        }
        const cat = t.closest('.atl-cat');
        const zone = t.closest('.atl-sub, .atl-feat');
        if (cat && zone) {
          const list = [...zone.querySelectorAll('a:not([tabindex="-1"])')].filter(vis);
          const i = list.indexOf(t);
          const isSub = zone.classList.contains('atl-sub');
          if (k === 'ArrowDown') cyc(list, i, 1).focus();
          else if (k === 'ArrowUp') cyc(list, i, -1).focus();
          else if (k === 'ArrowLeft') ((isSub ? null : [...cat.querySelectorAll('.atl-sub a')].find(vis)) || cat.querySelector('.atl-cat__a')).focus();
          else if (isSub) { const f = [...cat.querySelectorAll('.atl-feat a:not([tabindex="-1"])')].find(vis); if (f) f.focus(); }
          return;
        }
        const rest = [...st.panel.querySelectorAll(mini ? '.atl-brand, .atl-all' : '.atl-all, .atl__strip a')].filter(vis);
        const ri = rest.indexOf(t);
        if (ri < 0) return;
        if (/Right|Down/.test(k)) cyc(rest, ri, 1).focus();
        else if (mini || ri > 0) cyc(rest, ri, -1).focus();
        else { const l = st.panel.querySelector('.atl-cat.is-on .atl-cat__a'); if (l) l.focus(); }
      }, true);
    });
    if (!panels.length) return;

    // Entrée voisine : fermeture différée (le pointeur qui la frôle en diagonale vers le panneau ne ferme rien)
    items.forEach((li) => {
      if (!panels.some((p) => p.host === li)) on(li, 'pointerenter', (e) => { if (isMouse(e)) panels.forEach((p) => { clearTimeout(p.tOpen); if (p.open) p.later(); }); });
    });
    on(doc, 'keydown', (e) => { if (e.key === 'Escape') panels.forEach((p) => p.open && p.setOpen(false)); });
    on(window, 'pointerdown', (e) => {
      root.classList.toggle('atl-touch', e.pointerType === 'touch');
      panels.forEach((p) => { if (p.open && !p.host.contains(e.target)) p.setOpen(false); });
    }, { passive: true, capture: true });
    const regeo = () => panels.forEach((p) => { if (p.panel) geo(p.host, p.panel, p.mini); });
    on(window, 'resize', regeo, { passive: true });
    on(window, 'scroll', () => { if (panels.some((p) => p.open)) regeo(); }, { passive: true });
    if ('ResizeObserver' in window) { const ro = new ResizeObserver(regeo); ro.observe(nav); teardowns.push(() => ro.disconnect()); }

    // Construction à la première intention, ou quand le navigateur est libre
    const buildAll = () => panels.forEach((p) => p.build());
    on(nav, 'pointerenter', buildAll, { once: true });
    on(nav, 'focusin', buildAll, { once: true });
    let idleH = 0;
    const idle = () => { idleH = ric ? requestIdleCallback(buildAll, { timeout: 3000 }) : setTimeout(buildAll, 2000); };
    if (doc.readyState === 'complete') idle(); else on(window, 'load', idle, { once: true });
    teardowns.push(() => {
      if (ric) cancelIdleCallback(idleH); else clearTimeout(idleH);
      panels.forEach((p) => { clearTimeout(p.tClose); clearTimeout(p.tOpen); clearTimeout(p.aim); clearTimeout(p.tWake); });
    });

    /* ---------- langue + pays : une seule pastille « FR · CAD $ » ---------- */
    const full = doc.getElementById('SelvAtelierTpl');
    const right = header.querySelector('.site-header__wrapper__right');
    const icons = right && right.querySelector('.top-links--icon-links');
    const cForm = doc.getElementById('FooterCountryForm');
    const lForm = doc.getElementById('localization_form');
    if (full && full.dataset.atlLoc === '1' && right && icons && (cForm || lForm)) {
      const lang = ((lForm && lForm.querySelector('input[name="language_code"]')) || {}).value || root.lang || 'fr';
      const cBtn = cForm && cForm.querySelector('.disclosure__button span');
      const cur = cBtn ? (txt(cBtn).match(/\(([^)]+)\)/) || [null, 'CAD $'])[1] : 'CAD $';
      const opts = (form) => (form ? [...form.querySelectorAll('.disclosure__item_el')].map((a) => ({ v: a.dataset.value, t: txt(a), on: a.getAttribute('aria-current') === 'true' })) : []);
      const langs = opts(lForm);
      const countries = opts(cForm);
      const en = /^en/i.test(lang);
      const L = en ? { lang: 'Language', country: 'Country and currency', label: 'Language and currency' } : { lang: 'Langue', country: 'Pays et devise', label: 'Langue et devise' };
      const loc = doc.createElement('div');
      loc.className = 'atl-loc';
      loc.innerHTML = `<button type="button" class="atl-loc__btn" aria-expanded="false" aria-controls="atl-loc-pop" aria-label="${esc(L.label)} : ${esc(lang.toUpperCase())}, ${esc(cur)}"><span class="atl-loc__lang">${esc(lang.toUpperCase())}</span><i aria-hidden="true"></i><span>${esc(cur)}</span>${ARROW}</button>
        <div class="atl-loc__pop" id="atl-loc-pop" hidden>
          ${langs.length ? `<div class="atl-loc__col"><p class="atl-eyebrow">${esc(L.lang)}</p><ul role="list">${langs.map((o) => `<li><button type="button" data-f="l" data-v="${esc(o.v)}"${o.on ? ' aria-current="true"' : ''}>${esc(o.t)}</button></li>`).join('')}</ul></div>` : ''}
          ${countries.length ? `<div class="atl-loc__col"><p class="atl-eyebrow">${esc(L.country)}</p><ul role="list">${countries.map((o) => `<li><button type="button" data-f="c" data-v="${esc(o.v)}"${o.on ? ' aria-current="true"' : ''}>${esc(o.t)}</button></li>`).join('')}</ul></div>` : ''}
        </div>`;
      right.insertBefore(loc, icons);
      root.classList.add('atl-loc-on');
      teardowns.push(() => { loc.remove(); root.classList.remove('atl-loc-on'); closeLoc = null; });
      const lb = loc.querySelector('.atl-loc__btn');
      const pop = loc.querySelector('.atl-loc__pop');
      const show = (v) => { lb.setAttribute('aria-expanded', String(v)); pop.hidden = !v; };
      closeLoc = () => { if (!pop.hidden) show(false); };
      on(lb, 'click', () => {
        const v = pop.hidden;
        if (v) panels.forEach((p) => p.open && p.setOpen(false));
        show(v);
      });
      on(loc, 'keydown', (e) => { if (e.key === 'Escape' && !pop.hidden) { e.stopPropagation(); show(false); lb.focus(); } });
      on(loc, 'focusout', (e) => { if (!loc.contains(e.relatedTarget)) show(false); });
      on(window, 'pointerdown', (e) => { if (!loc.contains(e.target)) show(false); }, { passive: true });
      on(pop, 'click', (e) => {
        const b = e.target.closest('button[data-v]');
        if (!b || b.hasAttribute('aria-current')) return;
        const form = b.dataset.f === 'l' ? lForm : cForm;
        const input = form && form.querySelector(b.dataset.f === 'l' ? 'input[name="language_code"]' : 'input[name="country_code"]');
        if (!input) return;
        input.value = b.dataset.v;
        form.submit();
      });
    }
  };

  /* ---------- Téléphone et tablette (< 990 px) : « L'Atelier » en écrans dans le tiroir de menu ----------
     Le même <template> que le bureau fournit les données (catégories, photos, sous-liens, plus commandés, prix).
     « Nos produits » ouvre un écran ; chaque catégorie ouvre le sien ; retour par la flèche, Échap ou un glissé vers
     la droite. Les autres entrées du tiroir (Le Marché Selv, langue, devise, compte) restent celles du thème. */
  const ICON = {
    back: '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    close: '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    chev: '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow: '<svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" focusable="false"><path d="M4 10h11.5M11 5.5 15.5 10 11 14.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };
  const initMobile = () => {
    const tpl = doc.getElementById('SelvAtelierTpl');
    const drawer = doc.getElementById('NavDrawer');
    if (!tpl || !drawer) return;
    const want = path(tpl.dataset.atlParent || '');
    const item = [...drawer.querySelectorAll('.mobile-nav__item.top-level')].find((li) => {
      const a = li.querySelector(':scope > .mobile-nav__has-sublist > a.mobile-nav__link');
      return a && path(a.getAttribute('href') || '') === want;
    });
    if (!item) return;
    const head = item.querySelector(':scope > .mobile-nav__has-sublist');
    const link = head.querySelector('a.mobile-nav__link');
    const en = /^en/i.test(root.lang || '');
    const T = en ? { back: 'Back', close: 'Close menu', one: 'product', many: 'products' } : { back: 'Retour', close: 'Fermer le menu', one: 'produit', many: 'produits' };

    /* Feuille de style : préchargée quand le navigateur est libre (téléphone seulement) ; les écrans restent cachés tant
       qu'elle n'est pas arrivée, et le toucher sur « Nos produits » laisse alors l'accordéon du thème s'ouvrir. */
    let cssState = 0; // 0 rien, 1 en cours, 2 chargée, 3 échec
    let wrap = null;
    const loadCss = () => {
      if (cssState || !tpl.dataset.atlMcss) return;
      cssState = 1;
      const l = doc.createElement('link');
      l.rel = 'stylesheet';
      l.href = tpl.dataset.atlMcss;
      l.onload = () => { cssState = 2; if (wrap) wrap.hidden = false; };
      l.onerror = () => { cssState = 3; };
      doc.head.appendChild(l);
    };
    if (!desk()) {
      const pre = () => (ric ? requestIdleCallback(loadCss, { timeout: 4000 }) : setTimeout(loadCss, 2500));
      if (doc.readyState === 'complete') pre(); else on(window, 'load', pre, { once: true });
    }

    const layers = {};
    let stack = [];
    let shut = [];
    const el = (tag, cls, html) => { const n = doc.createElement(tag); if (cls) n.className = cls; if (html != null) n.innerHTML = html; return n; };
    const bar = (id, title) => `<div class="atlm__bar"><button type="button" class="atlm__back" data-atlm-back aria-label="${esc(T.back)}">${ICON.back}</button><p class="atlm__title" id="atlm-t-${id}" tabindex="-1">${esc(title)}</p><button type="button" class="atlm__close" data-atlm-close aria-label="${esc(T.close)}">${ICON.close}</button></div>`;
    const layer = (id, title) => {
      const L = el('section', 'atlm__layer', `${bar(id, title)}<div class="atlm__scroll"></div>`);
      L.id = `atlm-${id}`;
      L.dataset.l = id;
      L.setAttribute('aria-labelledby', `atlm-t-${id}`);
      L.inert = true;
      layers[id] = L;
      return L;
    };
    // Copie d'une image du modèle : tailles du téléphone, cadrage du bloc conservé ; widths = jeu de largeurs plus petit
    const img = (src, sizes, widths) => {
      if (!src) return null;
      const n = src.cloneNode(true);
      const op = src.style && src.style.objectPosition;
      n.removeAttribute('style');
      if (op) n.style.objectPosition = op;
      n.setAttribute('sizes', sizes);
      n.setAttribute('loading', 'lazy');
      const s = n.getAttribute('src');
      if (widths && s && /[?&]width=\d+/.test(s)) {
        n.setAttribute('srcset', widths.map((w) => `${s.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', '));
        n.setAttribute('src', s.replace(/([?&]width=)\d+/, `$1${widths[widths.length - 1]}`));
      }
      return n;
    };
    // Le contenu du tiroir sous les écrans ne doit être ni lu ni atteint au clavier
    const cover = (v) => {
      if (v) { shut = [...drawer.children].filter((c) => c !== wrap && !c.inert); shut.forEach((c) => { c.inert = true; }); }
      else { shut.forEach((c) => { c.inert = false; }); shut = []; }
    };

    const build = () => {
      if (wrap) return;
      const f = tpl.content;
      const cats = [...f.querySelectorAll('.atl-cat')];
      if (!cats.length) return;
      wrap = el('div', 'atlm');
      wrap.setAttribute('data-atlm', '');
      wrap.hidden = cssState !== 2;

      // Écran « Nos produits »
      const rootTitle = (txt(link) || (en ? 'Our products' : 'Nos produits')).toLowerCase().replace(/^./, (c) => c.toUpperCase());
      const R = layer('root', rootTitle);
      const rs = R.querySelector('.atlm__scroll');
      const all = f.querySelector('.atl-all');
      if (all) {
        const a = el('a', 'atlm__all', `<span>${esc(txt(all.querySelector('span')) || txt(all))}</span><span class="atlm__dot">${ICON.arrow}</span>`);
        a.href = all.getAttribute('href');
        rs.appendChild(a);
      }
      const ey = f.querySelector('.atl__z1 > .atl-eyebrow');
      if (ey) rs.appendChild(el('p', 'atlm__eyebrow', esc(txt(ey))));
      const ul = el('ul', 'atlm__cats');
      ul.setAttribute('role', 'list');
      cats.forEach((c, i) => {
        const id = `c${i}`;
        const name = txt(c.querySelector('.atl-cat__name'));
        const num = txt(c.querySelector('.atl-cat__num'));
        const nEl = c.querySelector('.atl-cat__n [aria-hidden]');
        const n = nEl ? parseInt(txt(nEl), 10) || 0 : 0;
        const li = el('li');
        li.style.setProperty('--i', i);
        const b = el('button', 'atlm__cat', `<span class="atlm__thumb" aria-hidden="true"></span><span class="atlm__cat-t"><span class="atlm__num" aria-hidden="true">${esc(num)}</span><span class="atlm__nm"><span class="atlm__name">${esc(name)}</span>${n ? `<span class="atlm__n"><span class="atl-sr">, </span>${n}<span class="atl-sr"> ${n === 1 ? T.one : T.many}</span></span>` : ''}</span></span><span class="atlm__chev">${ICON.chev}</span>`);
        b.type = 'button';
        b.dataset.go = id;
        b.setAttribute('aria-expanded', 'false');
        b.setAttribute('aria-controls', `atlm-${id}`);
        const th = img(c.querySelector('.atl-arch img'), '48px', [96, 144, 192]);
        if (th) b.querySelector('.atlm__thumb').appendChild(th);
        li.appendChild(b);
        ul.appendChild(li);

        // Écran de la catégorie
        const C = layer(id, name.toLowerCase().replace(/^./, (x) => x.toUpperCase()));
        const sc = C.querySelector('.atlm__scroll');
        const box = el('div', 'atlm__stagger');
        const pill = c.querySelector('.atl-pill');
        const hero = img(c.querySelector('.atl-arch img'), 'calc(min(90vw, 440px) - 40px)');
        if (hero) {
          const h = el('a', 'atlm__hero');
          h.href = c.querySelector('.atl-cat__a').getAttribute('href');
          h.tabIndex = -1;
          h.setAttribute('aria-hidden', 'true');
          h.appendChild(hero);
          box.appendChild(h);
        }
        const fe = c.querySelector('.atl-feat__col > .atl-eyebrow');
        if (fe) box.appendChild(el('p', 'atlm__eyebrow', esc(txt(fe))));
        const cap = c.querySelector('.atl-feat__cap');
        if (cap) box.appendChild(el('p', 'atlm__cap', esc(txt(cap))));
        const subList = c.querySelector('.atl-sub > ul');
        if (subList) { const s = el('div', 'atlm__subs'); s.appendChild(subList.cloneNode(true)); box.appendChild(s); }
        const best = c.querySelector('.atl-best');
        if (best) {
          const bb = el('div', 'atlm__best');
          bb.appendChild(el('p', 'atlm__eyebrow', esc(txt(best.querySelector('.atl-eyebrow')))));
          const bl = best.querySelector('.atl-best__list').cloneNode(true);
          [...bl.children].slice(2).forEach((x) => x.remove());
          bl.querySelectorAll('img').forEach((im) => { im.setAttribute('sizes', '60px'); im.setAttribute('loading', 'lazy'); });
          bb.appendChild(bl);
          box.appendChild(bb);
        }
        box.querySelectorAll('.atl-row__img img').forEach((im) => { im.setAttribute('sizes', '72px'); im.setAttribute('loading', 'lazy'); });
        [...box.children].forEach((x, k) => x.style.setProperty('--i', k));
        sc.appendChild(box);
        if (pill) {
          const cta = el('div', 'atlm__cta');
          const a = el('a', 'atlm__pill', `<span>${esc(txt(pill.querySelector('span')))}</span><span class="atlm__dot">${ICON.arrow}</span>`);
          a.href = pill.getAttribute('href');
          const sr = pill.querySelector('.atl-sr');
          if (sr) a.appendChild(sr.cloneNode(true));
          cta.appendChild(a);
          C.appendChild(cta);
        }
      });
      rs.appendChild(ul);
      const quick = [...f.querySelectorAll('.atl-quick__a')];
      if (quick.length) {
        const q = el('ul', 'atlm__quick');
        q.setAttribute('role', 'list');
        quick.forEach((a) => { const li = el('li'); li.appendChild(a.cloneNode(true)); q.appendChild(li); });
        rs.appendChild(q);
      }

      // Images en attente : aucune n'est téléchargée tant que son écran n'est pas montré (ou visé du doigt)
      Object.values(layers).forEach(park);
      wrap.appendChild(R);
      Object.keys(layers).filter((k) => k !== 'root').forEach((k) => wrap.appendChild(layers[k]));
      drawer.appendChild(wrap);

      wrap.addEventListener('click', (e) => {
        const t = e.target.closest('[data-atlm-back], [data-atlm-close], [data-go]');
        if (!t) return;
        if (t.hasAttribute('data-atlm-back')) back();
        else if (t.hasAttribute('data-atlm-close')) { reset(); const x = drawer.querySelector('.mobile-menu--close-btn'); if (x) x.click(); }
        else show(t.dataset.go, t);
      });
      // Le doigt qui se pose sur une catégorie lance déjà le téléchargement de son écran
      wrap.addEventListener('pointerdown', (e) => { const t = e.target.closest('[data-go]'); if (t) wake(layers[t.dataset.go]); }, { passive: true });
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && stack.length) { e.stopPropagation(); e.preventDefault(); escUp = true; back(); return; }
        // Tab tourne dans l'écran ouvert (le tiroir est modal ; le contenu caché dessous est inerte)
        const L = stack[stack.length - 1];
        if (e.key !== 'Tab' || !L) return;
        const f = [...L.querySelectorAll('a[href], button, [tabindex="0"]')].filter((x) => x.tabIndex > -1 && x.getClientRects().length);
        if (!f.length) return;
        const i = f.indexOf(doc.activeElement);
        if (i < 0) return;
        if (e.shiftKey && i === 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      });
      // Glisser vers la droite = retour (événements tactiles : touchend arrive même quand le navigateur annule le pointeur)
      let sx = 0; let sy = 0; let live = false;
      wrap.addEventListener('touchstart', (e) => { if (e.touches.length !== 1) { live = false; return; } sx = e.touches[0].clientX; sy = e.touches[0].clientY; live = true; }, { passive: true });
      wrap.addEventListener('touchend', (e) => {
        if (!live) return;
        live = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - sx; const dy = Math.abs(t.clientY - sy);
        if (dx > 70 && dy < 50 && dx > dy * 1.6) back();
      }, { passive: true });
    };

    // Échap : le thème ferme tout le tiroir au relâchement de la touche ; après un retour d'un écran, ce relâchement-là
    // est retenu (écouteur en capture sur window : le focus a parfois déjà quitté les écrans)
    let escUp = false;
    on(window, 'keyup', (e) => { if (e.key === 'Escape' && escUp) { escUp = false; e.stopImmediatePropagation(); } }, true);

    const focusTitle = (L, n = 0) => {
      const t = L.querySelector('.atlm__title');
      if (!t || !L.classList.contains('is-on')) return;
      t.focus({ preventScroll: true });
      if (doc.activeElement !== t && n < 8) setTimeout(() => focusTitle(L, n + 1), 40);
    };
    const show = (id, from) => {
      const next = layers[id];
      if (!next || stack[stack.length - 1] === next) return;
      wake(next);
      const cur = stack[stack.length - 1];
      if (cur) { cur.classList.remove('is-on'); cur.classList.add('is-behind'); cur.inert = true; }
      next.classList.remove('is-behind', 'is-leaving');
      next.inert = false;
      next.querySelector('.atlm__scroll').scrollTop = 0;
      void next.offsetWidth;
      next.classList.add('is-on');
      next.__from = from;
      if (from && from.setAttribute) from.setAttribute('aria-expanded', 'true');
      stack.push(next);
      setTimeout(() => focusTitle(next), reduce ? 0 : 120);
    };
    const reset = () => {
      Object.values(layers).forEach((L) => { L.classList.remove('is-on', 'is-behind', 'is-leaving'); L.inert = true; if (L.__from && L.__from.setAttribute) L.__from.setAttribute('aria-expanded', 'false'); });
      stack = [];
      cover(false);
      drawer.classList.remove('atlm-on');
    };
    const back = () => {
      const cur = stack.pop();
      if (!cur) return;
      // le contenu reste visible pendant que l'écran glisse vers la droite
      cur.classList.add('is-leaving');
      cur.classList.remove('is-on');
      cur.inert = true;
      setTimeout(() => cur.classList.remove('is-leaving'), reduce ? 0 : 480);
      const prev = stack[stack.length - 1];
      if (prev) { prev.classList.remove('is-behind'); prev.classList.add('is-on'); prev.inert = false; }
      else {
        cover(false);
        setTimeout(() => { if (!stack.length) drawer.classList.remove('atlm-on'); }, reduce ? 0 : 460);
      }
      const f = cur.__from;
      if (f && f.setAttribute) f.setAttribute('aria-expanded', 'false');
      if (f && f.focus) f.focus({ preventScroll: true });
    };
    const openRoot = () => {
      build();
      if (!wrap || stack.length) return;
      drawer.scrollTop = 0;
      drawer.classList.add('atlm-on');
      cover(true);
      show('root', link);
    };

    // « Nos produits » ouvre l'écran au lieu de l'accordéon (sans JavaScript ou sans la feuille, l'accordéon reste)
    const onTap = (e) => {
      if (desk()) return;
      loadCss();
      if (cssState !== 2) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openRoot();
    };
    on(head, 'click', onTap, true);
    link.setAttribute('aria-controls', 'atlm-root');
    link.setAttribute('aria-expanded', 'false');

    // Préparer à l'ouverture du tiroir (styles et écrans prêts au premier toucher, sans aucune image), réinitialiser à
    // sa fermeture
    const ham = doc.querySelector('.hamburger-nav-button, [aria-controls="NavDrawer"]');
    if (ham) on(ham, 'click', () => { loadCss(); requestAnimationFrame(build); });
    const mo = new MutationObserver(() => {
      if (!doc.body.classList.contains('js-drawer-open-left') && stack.length) setTimeout(() => { if (!doc.body.classList.contains('js-drawer-open-left')) reset(); }, 450);
    });
    mo.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    teardowns.push(() => { mo.disconnect(); cover(false); if (wrap) wrap.remove(); drawer.classList.remove('atlm-on'); link.removeAttribute('aria-controls'); link.removeAttribute('aria-expanded'); });
  };

  const teardown = () => {
    teardowns.forEach((f) => { try { f(); } catch (e) { /* rien */ } });
    teardowns = [];
    doc.querySelectorAll('#AccessibleNav > li > .atl').forEach((p) => p.remove());
    doc.querySelectorAll('#AccessibleNav > li.atl-host, #AccessibleNav > li.atl-host-mini').forEach((li) => {
      li.classList.remove('atl-host', 'atl-host-mini', 'atl-on', 'nav-hover');
      li.style.removeProperty('--atl-bridge');
      // le bouton de sous-menu retrouve son menu natif (selv-liquid.js)
      const b = li.querySelector(':scope > .lq-dd-btn');
      const dd = li.querySelector(':scope > .site-nav__dropdown');
      if (b) { b.setAttribute('aria-expanded', 'false'); if (dd && dd.id) b.setAttribute('aria-controls', dd.id); }
    });
    root.classList.remove('atl-ready', 'atl-full-open', 'atl-mini-open', 'atl-veil-on', 'atl-touch');
  };

  const start = () => { init(); initMobile(); };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  // Éditeur de thème : la section L'Atelier ou l'en-tête rechargés reconstruisent le menu ; retirés, ils le défont
  // (sans quoi les panneaux resteraient dans la barre, sans leur feuille de style)
  const ours = (t) => !!t && ((t.id || '').includes('selv_atelier') || !!(t.querySelector && t.querySelector('#SelvAtelierTpl, #SelvAtelierMiniTpl, header.site-header, #NavDrawer')));
  doc.addEventListener('shopify:section:load', (e) => { if (ours(e.target)) { teardown(); start(); } });
  doc.addEventListener('shopify:section:unload', (e) => { if (ours(e.target)) teardown(); });
  if (reduce) root.classList.add('atl-reduce');
})();
